import type { 
  Card, Suit, Rank, Player, GameState, HandRank, HandEvaluation 
} from './types';
import { AVATARS, PLAYER_NAMES } from './types';

// ============================================
// GESTIÓ DE LA BARALLA
// ============================================

export function createDeck(): Card[] {
  const suits: Suit[] = ['♠', '♥', '♦', '♣'];
  const ranks: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
  const deck: Card[] = [];
  
  for (const suit of suits) {
    for (const rank of ranks) {
      deck.push({ suit, rank });
    }
  }
  
  return deck;
}

export function shuffleDeck(deck: Card[]): Card[] {
  const shuffled = [...deck];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

export function drawCards(deck: Card[], count: number): { cards: Card[]; deck: Card[] } {
  return { cards: deck.slice(0, count), deck: deck.slice(count) };
}

// ============================================
// CREACIÓ DE JUGADORS
// ============================================

export function createPlayers(humanName: string, numBots: number = 4): Player[] {
  const usedAvatars = new Set<string>();
  const usedNames = new Set<string>();
  
  const getRandomAvatar = (): string => {
    const available = AVATARS.filter(a => !usedAvatars.has(a));
    const avatar = available[Math.floor(Math.random() * available.length)];
    usedAvatars.add(avatar);
    return avatar;
  };
  
  const getRandomName = (): string => {
    const available = PLAYER_NAMES.filter(n => !usedNames.has(n));
    const name = available[Math.floor(Math.random() * available.length)];
    usedNames.add(name);
    return name;
  };
  
  const players: Player[] = [];
  
  // Jugador humà sempre és "Tu"
  players.push({
    id: 'human',
    name: 'Tu',
    avatar: '👤',
    chips: 200,
    cards: [],
    bet: 0,
    totalBet: 0,
    folded: false,
    hasActed: false,
    isAllIn: false,
    isDealer: false,
    isCurrentPlayer: false,
    isHuman: true,
  });
  
  // Bots amb noms fixos: Usuari 2, Usuari 3, Usuari 4
  for (let i = 0; i < numBots; i++) {
    players.push({
      id: `bot-${i + 2}`,
      name: `Usuari ${i + 2}`,
      avatar: '🤖',
      chips: 200,
      cards: [],
      bet: 0,
      totalBet: 0,
      folded: false,
      hasActed: false,
      isAllIn: false,
      isDealer: false,
      isCurrentPlayer: false,
      isHuman: false,
    });
  }
  
  return players;
}

// ============================================
// AVALUACIÓ DE MANS
// ============================================

export function getCardValue(rank: Rank): number {
  const values: Record<Rank, number> = {
    '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10,
    'J': 11, 'Q': 12, 'K': 13, 'A': 14
  };
  return values[rank];
}

export function sortCards(cards: Card[]): Card[] {
  return [...cards].sort((a, b) => getCardValue(b.rank) - getCardValue(a.rank));
}

function countByRank(cards: Card[]): Map<Rank, Card[]> {
  const counts = new Map<Rank, Card[]>();
  for (const card of cards) {
    const existing = counts.get(card.rank) || [];
    counts.set(card.rank, [...existing, card]);
  }
  return counts;
}

function countBySuit(cards: Card[]): Map<Suit, Card[]> {
  const counts = new Map<Suit, Card[]>();
  for (const card of cards) {
    const existing = counts.get(card.suit) || [];
    counts.set(card.suit, [...existing, card]);
  }
  return counts;
}

function findStraight(cards: Card[]): Card[] | null {
  const sorted = sortCards(cards);
  const uniqueMap = new Map(sorted.map(c => [c.rank, c]));
  const unique = Array.from(uniqueMap.values());
  
  for (let i = 0; i <= unique.length - 5; i++) {
    const potential = unique.slice(i, i + 5);
    const values = potential.map(c => getCardValue(c.rank));
    if (values[0] - values[4] === 4) {
      return potential;
    }
  }
  
  // Escala baixa (A-2-3-4-5)
  const hasAce = unique.some(c => c.rank === 'A');
  const lowStraight = ['5', '4', '3', '2'].every(r => 
    unique.some(c => c.rank === r as Rank)
  );
  
  if (hasAce && lowStraight) {
    const ace = unique.find(c => c.rank === 'A')!;
    const lowCards = ['5', '4', '3', '2'].map(r => 
      unique.find(c => c.rank === r as Rank)!
    );
    return [...lowCards, ace];
  }
  
  return null;
}

export function evaluateHand(playerCards: Card[], communityCards: Card[]): HandEvaluation {
  const allCards = [...playerCards, ...communityCards];
  const byRank = countByRank(allCards);
  const bySuit = countBySuit(allCards);
  
  // Flush
  let flush: Card[] | null = null;
  const suitValues = Array.from(bySuit.values());
  for (const cards of suitValues) {
    if (cards.length >= 5) {
      flush = sortCards(cards).slice(0, 5);
      break;
    }
  }
  
  const straight = findStraight(allCards);
  
  // Escala de color / Escala reial
  if (flush) {
    const flushStraight = findStraight(flush);
    if (flushStraight) {
      const isRoyal = getCardValue(flushStraight[0].rank) === 14;
      return {
        rank: isRoyal ? 'Escala Reial' : 'Escala de Color',
        score: isRoyal ? 10 : 9,
        cards: flushStraight,
      };
    }
  }
  
  // Poker (4 iguals)
  const rankValues = Array.from(byRank.values());
  for (const cards of rankValues) {
    if (cards.length === 4) {
      const kicker = sortCards(allCards.filter(c => c.rank !== cards[0].rank))[0];
      return { rank: 'Poker', score: 8, cards: [...cards, kicker] };
    }
  }
  
  // Full
  const trips = Array.from(byRank.entries()).filter(([, c]) => c.length === 3);
  const pairs = Array.from(byRank.entries()).filter(([, c]) => c.length >= 2);
  
  if (trips.length > 0 && pairs.length > 1) {
    const tripCards = trips[0][1];
    const pairCards = pairs
      .filter(([r]) => r !== tripCards[0].rank)
      .flatMap(([, c]) => c);
    const bestPair = sortCards(pairCards).slice(0, 2);
    return { rank: 'Full', score: 7, cards: [...tripCards, ...bestPair] };
  }
  
  if (flush) {
    return { rank: 'Color', score: 6, cards: flush };
  }
  
  if (straight) {
    return { rank: 'Escala', score: 5, cards: straight };
  }
  
  // Trio
  if (trips.length > 0) {
    const tripCards = trips[0][1];
    const kickers = sortCards(allCards.filter(c => c.rank !== tripCards[0].rank)).slice(0, 2);
    return { rank: 'Trio', score: 4, cards: [...tripCards, ...kickers] };
  }
  
  // Doble parella
  const pairsOnly = pairs.filter(([, c]) => c.length === 2);
  if (pairsOnly.length >= 2) {
    const sortedPairs = pairsOnly.sort((a, b) => 
      getCardValue(b[0] as Rank) - getCardValue(a[0] as Rank)
    );
    const topTwo = sortedPairs.slice(0, 2).flatMap(([, c]) => c);
    const kicker = sortCards(allCards.filter(c => !topTwo.includes(c)))[0];
    return { rank: 'Doble Parella', score: 3, cards: [...topTwo, kicker] };
  }
  
  // Parella
  if (pairsOnly.length === 1) {
    const pairCards = pairsOnly[0][1];
    const kickers = sortCards(allCards.filter(c => c.rank !== pairCards[0].rank)).slice(0, 3);
    return { rank: 'Parella', score: 2, cards: [...pairCards, ...kickers] };
  }
  
  // Carta alta
  return { rank: 'Carta Alta', score: 1, cards: sortCards(allCards).slice(0, 5) };
}

// ============================================
// LÒGICA DEL JOC - TEXAS HOLD'EM
// ============================================

export function initializeGame(players: Player[], dealerIndex?: number): GameState {
  let deck = shuffleDeck(createDeck());
  const smallBlind = 5;
  const bigBlind = 10;
  
  // Dealer aleatori si no s'especifica
  const dealer = dealerIndex ?? Math.floor(Math.random() * players.length);
  
  // Reset jugadors
  const updatedPlayers = players.map((p, i) => ({
    ...p,
    isDealer: i === dealer,
    bet: 0,
    totalBet: 0,
    folded: false,
    hasActed: false,
    isAllIn: false,
    cards: [] as Card[],
    lastAction: undefined,
  }));
  
  // Repartir 2 cartes a cada jugador
  for (const player of updatedPlayers) {
    const { cards, deck: newDeck } = drawCards(deck, 2);
    player.cards = cards;
    deck = newDeck;
  }
  
  // Small i Big Blind
  const sbIndex = (dealer + 1) % players.length;
  const bbIndex = (dealer + 2) % players.length;
  
  // Posar blinds
  const sbAmount = Math.min(smallBlind, updatedPlayers[sbIndex].chips);
  updatedPlayers[sbIndex].bet = sbAmount;
  updatedPlayers[sbIndex].totalBet = sbAmount;
  updatedPlayers[sbIndex].chips -= sbAmount;
  updatedPlayers[sbIndex].lastAction = 'SB';
  
  const bbAmount = Math.min(bigBlind, updatedPlayers[bbIndex].chips);
  updatedPlayers[bbIndex].bet = bbAmount;
  updatedPlayers[bbIndex].totalBet = bbAmount;
  updatedPlayers[bbIndex].chips -= bbAmount;
  updatedPlayers[bbIndex].lastAction = 'BB';
  
  // Preparar cartes comunitàries (totes ocultes)
  const { cards: communityCards, deck: finalDeck } = drawCards(deck, 5);
  
  // El primer a actuar al preflop és UTG (després del BB)
  const firstToAct = (bbIndex + 1) % players.length;
  
  return {
    phase: 'preflop',
    players: updatedPlayers,
    communityCards: communityCards.map(c => ({ ...c, hidden: true })),
    pot: sbAmount + bbAmount,
    currentBet: bbAmount,
    minRaise: bigBlind,
    currentPlayerIndex: firstToAct,
    dealerIndex: dealer,
    smallBlind,
    bigBlind,
    deck: finalDeck,
    winner: null,
    winningHand: null,
    lastAggressor: bbIndex,
    roundStartPlayer: firstToAct,
  };
}

// Trobar el següent jugador actiu
export function getNextActivePlayer(players: Player[], currentIndex: number): number {
  const numPlayers = players.length;
  if (numPlayers === 0) return -1;
  
  let nextIndex = (currentIndex + 1) % numPlayers;
  let attempts = 0;
  
  // Buscar el següent jugador que pot actuar
  while (attempts < numPlayers) {
    const player = players[nextIndex];
    if (!player.folded && !player.isAllIn) {
      return nextIndex;
    }
    nextIndex = (nextIndex + 1) % numPlayers;
    attempts++;
  }
  
  // Si no trobem ningú actiu, retornar l'índex actual
  // (pot ser que tots estiguin all-in o hagin fet fold)
  return currentIndex;
}

// Comprovar si la ronda d'apostes ha acabat
export function isRoundComplete(state: GameState): boolean {
  const activePlayers = state.players.filter(p => !p.folded && !p.isAllIn);
  const nonFoldedPlayers = state.players.filter(p => !p.folded);
  
  // Si només queda 1 jugador no folded, la ronda ha acabat (guanya automàticament)
  if (nonFoldedPlayers.length <= 1) {
    return true;
  }
  
  // Si tots els jugadors no-folded estan all-in (0 jugadors actius), la ronda ha acabat
  if (activePlayers.length === 0) {
    return true;
  }
  
  // Si només queda 1 jugador actiu i tots els altres estan all-in o folded
  if (activePlayers.length === 1) {
    const activePlayer = activePlayers[0];
    // Si ha actuat i té l'aposta igualada o superior
    if (activePlayer.hasActed && activePlayer.bet >= state.currentBet) {
      return true;
    }
    // Si no hi ha aposta a igualar (currentBet és 0 o igual a la seva aposta)
    if (activePlayer.bet === state.currentBet) {
      return true;
    }
  }
  
  // Tots els jugadors actius han d'haver actuat
  const allHaveActed = activePlayers.every(p => p.hasActed);
  if (!allHaveActed) {
    return false;
  }
  
  // Tots els jugadors actius han de tenir la mateixa aposta (o haver igualat currentBet)
  const allBetsEqual = activePlayers.every(p => p.bet === state.currentBet);
  
  return allBetsEqual;
}

// Comprovar si queden jugadors per actuar
export function getPlayersLeftToAct(state: GameState): number {
  return state.players.filter(p => !p.folded && !p.isAllIn && !p.hasActed).length;
}

// Avançar a la següent fase
export function advancePhase(state: GameState): GameState {
  const phaseOrder: GameState['phase'][] = ['preflop', 'flop', 'turn', 'river', 'showdown'];
  const currentIndex = phaseOrder.indexOf(state.phase);
  const nextPhase = phaseOrder[currentIndex + 1];
  
  // Revelar cartes - crear una còpia profunda
  const communityCards = state.communityCards.map(c => ({ ...c, hidden: c.hidden }));
  
  if (nextPhase === 'flop') {
    communityCards[0] = { ...communityCards[0], hidden: false };
    communityCards[1] = { ...communityCards[1], hidden: false };
    communityCards[2] = { ...communityCards[2], hidden: false };
  } else if (nextPhase === 'turn') {
    communityCards[3] = { ...communityCards[3], hidden: false };
  } else if (nextPhase === 'river') {
    communityCards[4] = { ...communityCards[4], hidden: false };
  }
  
  // Reset apostes per la nova ronda
  const players = state.players.map(p => ({ 
    ...p, 
    bet: 0, 
    hasActed: p.folded || p.isAllIn, // Els que han fet fold o all-in ja "han actuat"
    lastAction: p.folded ? p.lastAction : (p.isAllIn ? 'All-in' : undefined),
  }));
  
  // El primer a actuar és el primer jugador actiu després del dealer
  let firstToAct = (state.dealerIndex + 1) % players.length;
  let attempts = 0;
  while ((players[firstToAct].folded || players[firstToAct].isAllIn) && attempts < players.length) {
    firstToAct = (firstToAct + 1) % players.length;
    attempts++;
  }
  
  // Si no hi ha ningú que pugui actuar (tots all-in o folded), mantenir l'índex actual
  const hasActivePlayer = players.some(p => !p.folded && !p.isAllIn);
  if (!hasActivePlayer) {
    firstToAct = state.currentPlayerIndex;
  }
  
  return {
    ...state,
    phase: nextPhase,
    communityCards,
    players,
    currentBet: 0,
    minRaise: state.bigBlind,
    currentPlayerIndex: firstToAct,
    lastAggressor: -1,
    roundStartPlayer: firstToAct,
  };
}

// Processar una acció del jugador
export function processAction(
  state: GameState, 
  playerIndex: number, 
  action: 'fold' | 'check' | 'call' | 'raise',
  raiseAmount?: number
): GameState {
  const players = state.players.map(p => ({ ...p }));
  const player = players[playerIndex];
  let pot = state.pot;
  let currentBet = state.currentBet;
  let minRaise = state.minRaise;
  let lastAggressor = state.lastAggressor;
  
  switch (action) {
    case 'fold':
      player.folded = true;
      player.hasActed = true;
      player.lastAction = 'Retira';
      break;
      
    case 'check':
      player.hasActed = true;
      player.lastAction = 'Passa';
      break;
      
    case 'call':
      const callAmount = Math.min(currentBet - player.bet, player.chips);
      player.chips -= callAmount;
      player.bet += callAmount;
      player.totalBet += callAmount;
      pot += callAmount;
      player.hasActed = true;
      
      if (player.chips === 0) {
        player.isAllIn = true;
        player.lastAction = 'All-in';
      } else {
        player.lastAction = 'Iguala';
      }
      break;
      
    case 'raise':
      const totalRaise = raiseAmount || (currentBet + minRaise);
      const raiseToAmount = Math.min(totalRaise, player.bet + player.chips);
      const additionalBet = raiseToAmount - player.bet;
      
      player.chips -= additionalBet;
      player.bet = raiseToAmount;
      player.totalBet += additionalBet;
      pot += additionalBet;
      
      // Actualitzar mínim per pujar
      const raiseSize = raiseToAmount - currentBet;
      if (raiseSize > minRaise) {
        minRaise = raiseSize;
      }
      
      currentBet = raiseToAmount;
      lastAggressor = playerIndex;
      
      // Resetejar hasActed de tots els altres
      players.forEach((p, i) => {
        if (i !== playerIndex && !p.folded && !p.isAllIn) {
          p.hasActed = false;
        }
      });
      
      player.hasActed = true;
      
      if (player.chips === 0) {
        player.isAllIn = true;
        player.lastAction = 'All-in';
      } else {
        player.lastAction = state.currentBet === 0 ? 'Aposta' : 'Puja';
      }
      break;
  }
  
  // Trobar el següent jugador
  const nextIndex = getNextActivePlayer(players, playerIndex);
  
  return {
    ...state,
    players,
    pot,
    currentBet,
    minRaise,
    lastAggressor,
    currentPlayerIndex: nextIndex === -1 ? playerIndex : nextIndex,
  };
}

// ============================================
// IA DELS BOTS
// ============================================

export function makeBotDecision(
  player: Player,
  gameState: GameState
): { action: 'fold' | 'check' | 'call' | 'raise'; amount?: number } {
  const visibleCards = gameState.communityCards.filter(c => !c.hidden);
  const hand = evaluateHand(player.cards, visibleCards);
  const toCall = gameState.currentBet - player.bet;
  
  // Funció per arrodonir a múltiples de 5
  const roundToChipDenomination = (amount: number): number => {
    return Math.max(5, Math.round(amount / 5) * 5);
  };
  
  // Força de la mà (0-1)
  const handStrength = hand.score / 10;
  
  // Potencial de la mà
  const potential = visibleCards.length < 5 ? 0.15 : 0;
  const effectiveStrength = Math.min(1, handStrength + potential);
  
  // Factor aleatori
  const randomFactor = Math.random() * 0.25 - 0.125;
  const adjustedStrength = effectiveStrength + randomFactor;
  
  // Si podem passar
  if (toCall === 0) {
    if (adjustedStrength > 0.55 && Math.random() < 0.4) {
      const raiseAmount = roundToChipDenomination(
        Math.min(
          Math.round(gameState.pot * (0.4 + adjustedStrength * 0.4)),
          player.chips
        )
      );
      if (raiseAmount >= gameState.minRaise) {
        return { action: 'raise', amount: gameState.currentBet + raiseAmount };
      }
    }
    return { action: 'check' };
  }
  
  // Hem de pagar
  
  // Mà molt dolenta
  if (adjustedStrength < 0.2) {
    return Math.random() < 0.8 ? { action: 'fold' } : { action: 'call' };
  }
  
  // Mà regular
  if (adjustedStrength < 0.45) {
    if (toCall <= gameState.bigBlind * 3) {
      return { action: 'call' };
    }
    return Math.random() < 0.6 ? { action: 'fold' } : { action: 'call' };
  }
  
  // Bona mà
  if (adjustedStrength < 0.65) {
    if (Math.random() < 0.25 && player.chips > toCall * 2) {
      const raiseAmount = roundToChipDenomination(
        Math.min(toCall + gameState.pot * 0.35, player.chips)
      );
      return { action: 'raise', amount: gameState.currentBet + raiseAmount };
    }
    return { action: 'call' };
  }
  
  // Molt bona mà
  if (Math.random() < 0.55 && player.chips > toCall * 2) {
    const raiseAmount = roundToChipDenomination(
      Math.min(toCall + gameState.pot * 0.5, player.chips)
    );
    return { action: 'raise', amount: gameState.currentBet + raiseAmount };
  }
  
  return { action: 'call' };
}

// ============================================
// DETERMINACIÓ DEL GUANYADOR
// ============================================

export function determineWinner(state: GameState): { winner: Player; hand: HandRank } {
  const activePlayers = state.players.filter(p => !p.folded);
  
  if (activePlayers.length === 1) {
    return { winner: activePlayers[0], hand: 'Carta Alta' };
  }
  
  const visibleCommunity = state.communityCards.filter(c => !c.hidden);
  
  let bestPlayer = activePlayers[0];
  let bestHand = evaluateHand(bestPlayer.cards, visibleCommunity);
  
  for (let i = 1; i < activePlayers.length; i++) {
    const player = activePlayers[i];
    const hand = evaluateHand(player.cards, visibleCommunity);
    
    if (hand.score > bestHand.score) {
      bestPlayer = player;
      bestHand = hand;
    } else if (hand.score === bestHand.score) {
      const currentHighest = Math.max(...hand.cards.map(c => getCardValue(c.rank)));
      const bestHighest = Math.max(...bestHand.cards.map(c => getCardValue(c.rank)));
      if (currentHighest > bestHighest) {
        bestPlayer = player;
        bestHand = hand;
      }
    }
  }
  
  return { winner: bestPlayer, hand: bestHand.rank };
}

// ============================================
// UTILITATS
// ============================================

export function isRedSuit(suit: Suit): boolean {
  return suit === '♥' || suit === '♦';
}

export function getActivePlayers(state: GameState): Player[] {
  return state.players.filter(p => !p.folded);
}

export function canPlayerCheck(state: GameState, playerIndex: number): boolean {
  return state.currentBet === state.players[playerIndex].bet;
}

// Calcular outs i probabilitat de millorar la mà
export function calculateOuts(
  playerCards: Card[], 
  communityCards: Card[]
): { outs: number; draws: string[] } {
  const allCards = [...playerCards, ...communityCards];
  const suits = allCards.map(c => c.suit);
  const ranks = allCards.map(c => c.rank);
  
  // Cartes ja utilitzades
  const usedCards = new Set(allCards.map(c => `${c.rank}${c.suit}`));
  
  let outs = 0;
  const draws: string[] = [];
  
  // Valor numèric de cada carta
  const rankValue = (r: Rank): number => {
    const values: Record<Rank, number> = {
      '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, 
      '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14
    };
    return values[r];
  };
  
  // =====================
  // 1. PROJECTE DE COLOR (Flush Draw)
  // =====================
  const suitCounts: Record<Suit, number> = { '♠': 0, '♥': 0, '♦': 0, '♣': 0 };
  suits.forEach(s => suitCounts[s]++);
  
  for (const [suit, count] of Object.entries(suitCounts) as [Suit, number][]) {
    if (count === 4) {
      // 13 cartes del pal - 4 que ja tens = 9 outs
      let flushOuts = 0;
      const allRanks: Rank[] = ['2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K', 'A'];
      for (const rank of allRanks) {
        if (!usedCards.has(`${rank}${suit}`)) {
          flushOuts++;
        }
      }
      outs += flushOuts;
      draws.push(`Color (${flushOuts} outs)`);
    }
  }
  
  // =====================
  // 2. PROJECTE D'ESCALA
  // =====================
  const uniqueValues = Array.from(new Set(ranks.map(rankValue))).sort((a, b) => a - b);
  
  // Escala oberta (Open-Ended Straight Draw) - 8 outs
  // Ex: 5-6-7-8 necessita 4 o 9
  for (let i = 0; i <= uniqueValues.length - 4; i++) {
    const seq = uniqueValues.slice(i, i + 4);
    if (seq.length === 4) {
      const isConsecutive = seq.every((v, idx) => idx === 0 || v === seq[idx - 1] + 1);
      if (isConsecutive) {
        const lowCard = seq[0] - 1;
        const highCard = seq[3] + 1;
        let straightOuts = 0;
        
        // Comptar outs (4 cartes de cada valor, menys les utilitzades)
        if (lowCard >= 2) {
          const lowRank = Object.entries({ '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 })
            .find(([_, v]) => v === lowCard)?.[0] as Rank;
          if (lowRank) {
            for (const s of ['♠', '♥', '♦', '♣'] as Suit[]) {
              if (!usedCards.has(`${lowRank}${s}`)) straightOuts++;
            }
          }
        }
        if (highCard <= 14) {
          const highRank = Object.entries({ '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 })
            .find(([_, v]) => v === highCard)?.[0] as Rank;
          if (highRank) {
            for (const s of ['♠', '♥', '♦', '♣'] as Suit[]) {
              if (!usedCards.has(`${highRank}${s}`)) straightOuts++;
            }
          }
        }
        
        if (straightOuts > 0 && !draws.some(d => d.includes('Escala'))) {
          outs += straightOuts;
          draws.push(`Escala oberta (${straightOuts} outs)`);
        }
      }
    }
  }
  
  // Escala interior (Gutshot) - 4 outs
  // Ex: 5-6-8-9 necessita 7
  if (!draws.some(d => d.includes('Escala'))) {
    for (let i = 0; i <= uniqueValues.length - 4; i++) {
      const window = uniqueValues.slice(i, i + 5);
      if (window.length >= 4) {
        const min = Math.min(...window);
        const max = Math.max(...window);
        if (max - min === 4) {
          // Buscar el forat
          for (let v = min; v <= max; v++) {
            if (!uniqueValues.includes(v)) {
              const gapRank = Object.entries({ '2': 2, '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9, '10': 10, 'J': 11, 'Q': 12, 'K': 13, 'A': 14 })
                .find(([_, val]) => val === v)?.[0] as Rank;
              if (gapRank) {
                let gutshotOuts = 0;
                for (const s of ['♠', '♥', '♦', '♣'] as Suit[]) {
                  if (!usedCards.has(`${gapRank}${s}`)) gutshotOuts++;
                }
                if (gutshotOuts > 0) {
                  outs += gutshotOuts;
                  draws.push(`Escala interior (${gutshotOuts} outs)`);
                }
              }
              break;
            }
          }
        }
      }
    }
  }
  
  // =====================
  // 3. MILLORAR A PARELLA/TRIO
  // =====================
  const rankCounts: Record<string, number> = {};
  ranks.forEach(r => rankCounts[r] = (rankCounts[r] || 0) + 1);
  
  const pairs = Object.entries(rankCounts).filter(([_, c]) => c === 2);
  const hasTrips = Object.values(rankCounts).some(c => c === 3);
  
  // Si tens parella, pots fer trio (2 outs per parella)
  if (pairs.length === 1 && !hasTrips) {
    const pairRank = pairs[0][0] as Rank;
    let tripOuts = 0;
    for (const s of ['♠', '♥', '♦', '♣'] as Suit[]) {
      if (!usedCards.has(`${pairRank}${s}`)) tripOuts++;
    }
    if (tripOuts > 0) {
      outs += tripOuts;
      draws.push(`Trio (${tripOuts} outs)`);
    }
  }
  
  // Si no tens res, comptar outs per fer parella amb les cartes altes
  if (Object.values(rankCounts).every(c => c === 1)) {
    // Només les dues cartes del jugador
    const highCards = playerCards
      .map(c => c.rank)
      .filter(r => rankValue(r) >= 10); // J, Q, K, A
    
    if (highCards.length > 0) {
      let pairOuts = 0;
      for (const rank of highCards) {
        for (const s of ['♠', '♥', '♦', '♣'] as Suit[]) {
          if (!usedCards.has(`${rank}${s}`)) pairOuts++;
        }
      }
      if (pairOuts > 0 && draws.length === 0) {
        outs += pairOuts;
        draws.push(`Parella alta (${pairOuts} outs)`);
      }
    }
  }
  
  return { outs, draws };
}

export function calculateWinProbability(
  playerCards: Card[], 
  communityCards: Card[], 
  _numOpponents: number
): number {
  const visibleCommunity = communityCards.filter(c => !c.hidden);
  const hand = evaluateHand(playerCards, visibleCommunity);
  
  // Si ja tens una mà forta, probabilitat alta
  if (hand.score >= 5) { // Color o millor
    return Math.min(95, 70 + hand.score * 3);
  }
  
  if (hand.score >= 3) { // Trio o millor
    return Math.min(85, 50 + hand.score * 5);
  }
  
  // Calcular outs
  const { outs } = calculateOuts(playerCards, visibleCommunity);
  
  // Regla del 4 i 2
  const cardsTocome = 5 - visibleCommunity.length;
  let probability: number;
  
  if (cardsTocome === 2) {
    // Flop: outs × 4
    probability = outs * 4;
  } else if (cardsTocome === 1) {
    // Turn: outs × 2
    probability = outs * 2;
  } else {
    // Preflop o showdown
    probability = 20 + (hand.score * 5);
  }
  
  // Ajustar segons la mà actual
  probability += hand.score * 3;
  
  // Limitar entre 5% i 95%
  return Math.min(95, Math.max(5, Math.round(probability)));
}
